<template>
  <v-card variant="flat" class="rounded-xl" :loading="loading">
    <v-card-title class="d-flex justify-space-between align-center">
      <div>
        <span>运行策略</span>
        <span class="ml-8 text-orange-darken-1">当前策略模式：{{ currentStrategyModeText }}</span>
      </div>
      <v-tabs v-model="tab" align-tabs="center" color="var(--color-primary)" style="max-width: 500px;">
        <v-tab :value="0">拟合</v-tab>
        <v-tab v-for="eLink in strategyData.eLinks || []" :key="eLink.elink_id" :value="eLink.elink_id">
          {{ eLink.elink_name }}
        </v-tab>
      </v-tabs>
    </v-card-title>
    <v-card-text>
      <v-chart :option="option" :autoresize="true" style="height: 300px;" />
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, computed, inject, watch } from 'vue'
import VChart from 'vue-echarts'
import { getStationStrategy } from '@/api/station'

const tab = ref(0)
const stationId = inject('stationId')

function normalizeStrategyItem(item) {
  return {
    startTime: item?.startTime ?? item?.start_time ?? '00:00',
    endTime: item?.endTime ?? item?.end_time ?? '00:00',
    power: Number(item?.power ?? 0),
    upperLimit: Number(item?.upperLimit ?? item?.upper_limit ?? 100),
    lowerLimit: Number(item?.lowerLimit ?? item?.lower_limit ?? 0)
  }
}

function normalizeStrategyList(list) {
  if (!Array.isArray(list)) return []
  return list.map(normalizeStrategyItem)
}

function getCurrentStrategyByType(elink) {
  if (!elink) return []
  if (Array.isArray(elink.strategys_current) && elink.strategys_current.length) return elink.strategys_current
  if (elink.strategy_type === 'work') return elink.strategys_work || []
  if (elink.strategy_type === 'rest') return elink.strategys_rest || []
  return []
}

const strategyData = ref({
  eLinks: [],
  station: []
})

const selectedStrategy = computed(() => {
  if (tab.value === 0) {
    const station = strategyData.value?.station || []
    console.log('station', station);

    return normalizeStrategyList(station)
  }

  const selectedELink = (strategyData.value?.eLinks || []).find(item => item.elink_id === tab.value)
  const normalized = normalizeStrategyList(getCurrentStrategyByType(selectedELink))
  return normalized.length ? normalized : []
})

const currentStrategyModeText = computed(() => {
  // if (tab.value === 0) return '削峰填谷'
  // const selectedELink = (strategyData.value?.eLinks || []).find(item => item.elink_id === tab.value)
  // const mode = selectedELink?.strategy_type
  // if (mode === 'work') return '工作日'
  // if (mode === 'rest') return '休息日'
  return '削峰填谷'
})

function timeToDateTimestamp(time) {
  const [hour, minute] = (time || '00:00').split(':').map(v => Number(v) || 0)
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute, 0).getTime()
}

function getYAxisRange(data) {
  if (!data.length) return { min: -100, max: 100 }
  const powers = data.map(item => Number(item.power) || 0)
  const maxAbs = Math.max(...powers.map(v => Math.abs(v)), 100)
  const pad = Math.ceil(maxAbs * 0.1)
  return { min: -maxAbs - pad, max: maxAbs + pad }
}

const option = computed(() => {
  const normalizedData = selectedStrategy.value
  const seriesData = normalizedData.map(item => {
    const start = timeToDateTimestamp(item.startTime)
    const end = timeToDateTimestamp(item.endTime)
    const power = Number(item.power)
    return {
      value: [start, end, power, item.upperLimit, item.lowerLimit, item.startTime, item.endTime],
      itemStyle: {
        color: power > 0 ? 'rgb(255, 69, 0)' : (power < 0 ? '#4BB450' : '#BCC6DD')
      }
    }
  })

  const now = new Date()
  const min = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0).getTime()
  const max = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0).getTime()
  const yRange = getYAxisRange(normalizedData)

  return {
    tooltip: {
      trigger: 'item',
      formatter(params) {
        const [, , power, upper, lower, startText, endText] = params?.data?.value || []
        return `开始时间: ${startText}<br/>结束时间: ${endText}<br/>功率(kW): ${power}<br/>充电上限(%): ${upper}<br/>放电下限(%): ${lower}`
      }
    },
    xAxis: {
      type: 'time',
      min,
      max,
      splitNumber: 12,
      axisLabel: {
        formatter(value) {
          const time = new Date(value)
          const hour = time.getHours()
          const minute = time.getMinutes()
          if (hour % 2 !== 0 || minute !== 0) return ''
          const h = `${hour}`.padStart(2, '0')
          const m = `${minute}`.padStart(2, '0')
          return `${h}:${m}`
        }
      },
      nameTextStyle: {
        color: '#999'
      },
    },
    yAxis: {
      type: 'value',
      min: yRange.min,
      max: yRange.max,
      nameTextStyle: {
        color: '#999'
      },
      splitLine: {
        lineStyle: {
          type: 'dashed',
          color: '#eee'
        }
      }
    },
    series: {
      type: 'custom',
      renderItem(params, api) {
        const yValue = api.value(2)
        const startPoint = api.coord([api.value(0), yValue])
        const size = api.size([api.value(1) - api.value(0), yValue])
        return {
          type: 'rect',
          shape: {
            x: startPoint[0],
            y: startPoint[1] <= 0 ? startPoint[1] + 4 : startPoint[1] - 4,
            width: size[0],
            height: 8
          },
          style: api.style()
        }
      },
      encode: {
        x: [0, 1],
        y: 2,
        tooltip: [0, 1, 2]
      },
      data: seriesData
    },
    grid: {
      top: 30,
      left: 50,
      bottom: 50,
      right: 10
    }
  }
})

const loading = ref(false)
async function fetchStationStrategy() {
  try {
    loading.value = true
    const res = await getStationStrategy(stationId.value)
    strategyData.value = res.data || {
      eLinks: [],
      station: []
    }

    console.log('strategyData', strategyData.value);

  } catch (e) {
    console.error('fetchStationStrategy error', e);
  }
  finally {
    loading.value = false
  }
}

fetchStationStrategy()


watch(stationId, () => {
  fetchStationStrategy()
})

</script>
